import type { MasterFieldDef, MasterSchema } from '../constants/masters';
import type { MasterFormState } from '../components/masters/MasterFormDrawer';
import { TIME_PATTERN, PHONE_PATTERN } from '../constants/foodMasterImageSpecs';
import { TRANSPORT_LETTER_ONLY_NAME_TYPES, TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES, TRANSPORT_INTEGER_SORT_ORDER_TYPES, TRANSPORT_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_DESCRIPTION_VALIDATED_TYPES, filterRecordsForParentScopedSortOrder, validateIntegerSortOrder, validatePassengerCapacity, validateVehicleServiceTypes, validateRideTagColor, parseCommaSeparatedDayValues } from '../constants/transportSubserviceMasters';
import { validatePersonName } from './personFieldValidation';
import { validateOptionalDescription } from './descriptionValidation';

export type FieldErrors = Record<string, string>;

function fieldErrorKey(key: string, isMeta: boolean) {
  return isMeta ? `meta:${key}` : key;
}

function validateField(
  field: MasterFieldDef,
  value: string | number | boolean | undefined,
  isMeta: boolean,
  errors: FieldErrors,
) {
  const key = fieldErrorKey(field.key, isMeta);
  const str = value === undefined || value === null ? '' : String(value);
  const isEmpty = str.trim() === '' && typeof value !== 'boolean' && typeof value !== 'number';

  if (field.required && isEmpty) {
    errors[key] = `${field.label} is required`;
    return;
  }
  if (isEmpty) return;

  if (field.type === 'number' || field.type === 'currency' || field.type === 'percent') {
    const num = Number(value);
    if (Number.isNaN(num)) {
      errors[key] = `${field.label} must be a number`;
      return;
    }
    if (field.key === 'capacity' && !Number.isInteger(num)) {
      errors[key] = `${field.label} must be a whole number (no decimals)`;
      return;
    }
    if (field.min !== undefined && num < field.min) {
      errors[key] = `${field.label} must be at least ${field.min}`;
    }
    if (field.max !== undefined && num > field.max) {
      errors[key] = `${field.label} must be at most ${field.max}`;
    }
    if (field.type === 'percent' && (num < 0 || num > 100)) {
      errors[key] = `${field.label} must be between 0 and 100`;
    }
  }

  if (field.pattern) {
    const re = new RegExp(field.pattern);
    if (!re.test(str)) {
      errors[key] = field.patternMessage || `${field.label} format is invalid`;
    }
  }

  if (field.key === 'openTime' || field.key === 'closeTime' || field.key === 'startTime' || field.key === 'endTime') {
    if (!TIME_PATTERN.test(str)) {
      errors[key] = 'Use 24-hour format HH:mm (e.g. 10:00, 22:30)';
    }
  }

  if (field.key === 'phone' && !PHONE_PATTERN.test(str)) {
    errors[key] = 'Phone number can only contain digits (8–15), with an optional leading +';
  }
}

export function validateMasterForm(
  form: MasterFormState,
  schema: (Pick<MasterSchema, 'type' | 'label' | 'metadataFields'> & Partial<MasterSchema>) | null | undefined,
  opts?: {
    hasParent?: boolean;
    requiredSortOrder?: number;
    existingRecords?: Array<{ _id?: string; code?: string; sortOrder?: number; parentId?: string; attributeCode?: string }>;
    editingId?: string;
    masterType?: string;
    sortOrderParentId?: string;
    sortOrderAttributeCode?: string;
  },
): FieldErrors {
  const errors: FieldErrors = {};
  const masterType = schema?.type ?? opts?.masterType;

  if (!form.code?.trim()) errors.code = 'Code is required';
  else if (!/^[A-Za-z0-9_]+$/.test(form.code.trim())) {
    errors.code = 'Code may only contain letters, numbers, and underscores';
  } else if (opts?.existingRecords?.length) {
    const duplicateErr = validateDuplicateMasterCode(
      form.code,
      opts.existingRecords,
      opts.editingId,
    );
    if (duplicateErr) errors.code = duplicateErr;
  }

  if (masterType && TRANSPORT_LETTER_ONLY_NAME_TYPES.has(masterType)) {
    const nameErr = validatePersonName(form.name, 'Display name', { required: true });
    if (nameErr) errors.name = nameErr;
  } else if (!form.name?.trim()) {
    errors.name = 'Display name is required';
  }

  if (masterType && TRANSPORT_INTEGER_SORT_ORDER_TYPES.has(masterType)) {
    const sortErr = validateIntegerSortOrder(form.sortOrder);
    if (sortErr) {
      errors.sortOrder = sortErr;
    } else if (
      TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(masterType)
      && opts?.requiredSortOrder != null
      && form.sortOrder !== opts.requiredSortOrder
    ) {
      errors.sortOrder = `Sort order must be ${opts.requiredSortOrder} — the next number in sequence`;
    } else if (
      TRANSPORT_UNIQUE_SORT_ORDER_TYPES.has(masterType)
      || TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)
    ) {
      const scopedRecords = filterRecordsForParentScopedSortOrder(
        masterType,
        opts?.existingRecords || [],
        {
          parentId: opts?.sortOrderParentId,
          attributeCode: opts?.sortOrderAttributeCode,
        },
      );
      const duplicateErr = validateDuplicateSortOrder(
        form.sortOrder,
        scopedRecords,
        opts?.editingId,
      );
      if (duplicateErr) errors.sortOrder = duplicateErr;
    }
  }

  if (masterType && TRANSPORT_DESCRIPTION_VALIDATED_TYPES.has(masterType)) {
    const descriptionErr = validateOptionalDescription(form.description);
    if (descriptionErr) errors.description = descriptionErr;
  }

  if (opts?.hasParent && !form.parentId) {
    errors.parentId = 'Parent master is required';
  }

  for (const field of schema?.metadataFields || []) {
    validateField(field, form.metadata[field.key], true, errors);
  }

  if (schema?.type === 'vehicle_types') {
    const capacityErr = validatePassengerCapacity(form.metadata.capacity, 'Passenger Capacity', { required: true });
    if (capacityErr) errors['meta:capacity'] = capacityErr;

    const serviceTypesErr = validateVehicleServiceTypes(form.metadata.serviceTypes);
    if (serviceTypesErr) errors['meta:serviceTypes'] = serviceTypesErr;
  }

  if (schema?.type === 'ride_tags') {
    const colorErr = validateRideTagColor(form.metadata.color, 'Tag Color');
    if (colorErr) errors['meta:color'] = colorErr;
  }

  const meta = form.metadata;
  if (schema?.type === 'addon_groups') {
    const min = Number(meta.minSelection ?? 0);
    const max = Number(meta.maxSelection ?? 0);
    if (max > 0 && min > max) {
      errors['meta:maxSelection'] = 'Max selection must be greater than or equal to min selection';
    }
  }

  if (schema?.type === 'restaurant_opening_hours' && !meta.isClosed) {
    const open = String(meta.openTime || '');
    const close = String(meta.closeTime || '');
    if (open && close && TIME_PATTERN.test(open) && TIME_PATTERN.test(close) && open >= close) {
      errors['meta:closeTime'] = 'Closing time must be after opening time';
    }
  }

  if (schema?.type === 'food_availability_schedules' || schema?.type === 'service_availability_schedules') {
    const start = String(meta.startTime || '');
    const end = String(meta.endTime || '');
    if (!start.trim()) {
      errors['meta:startTime'] = 'Start time is required';
    } else if (!TIME_PATTERN.test(start)) {
      errors['meta:startTime'] = 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)';
    }
    if (!end.trim()) {
      errors['meta:endTime'] = 'End time is required';
    } else if (!TIME_PATTERN.test(end)) {
      errors['meta:endTime'] = 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)';
    }
    if (
      start && end
      && TIME_PATTERN.test(start)
      && TIME_PATTERN.test(end)
      && start >= end
    ) {
      errors['meta:endTime'] = 'End time must be after start time';
    }
  }

  if (schema?.type === 'service_availability_schedules') {
    const days = parseCommaSeparatedDayValues(form.metadata.daysOfWeek);
    if (!days.length) {
      errors['meta:daysOfWeek'] = 'Select at least one day';
    }
  }

  return errors;
}

export function isMasterFormValid(errors: FieldErrors): boolean {
  return Object.keys(errors).length === 0;
}

export function isDuplicateMasterCode(
  code: string,
  records: Array<{ _id?: string; code?: string }>,
  excludeId?: string,
): boolean {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return false;
  return records.some((row) => {
    if (excludeId && row._id === excludeId) return false;
    return String(row.code || '').trim().toUpperCase() === normalized;
  });
}

export function validateDuplicateMasterCode(
  code: string,
  records: Array<{ _id?: string; code?: string }>,
  excludeId?: string,
  label = 'Code',
): string | undefined {
  if (!code.trim()) return undefined;
  if (isDuplicateMasterCode(code, records, excludeId)) {
    return `${label} is already used by another record`;
  }
  return undefined;
}

export function isDuplicateSortOrder(
  sortOrder: number,
  records: Array<{ _id?: string; sortOrder?: number }> = [],
  excludeId?: string,
): boolean {
  if (!Number.isFinite(sortOrder) || !Number.isInteger(sortOrder) || sortOrder < 1) return false;
  return records.some((row) => {
    if (excludeId && row._id === excludeId) return false;
    const existing = Number(row.sortOrder);
    return Number.isFinite(existing) && existing === sortOrder;
  });
}

export function validateDuplicateSortOrder(
  sortOrder: number,
  records: Array<{ _id?: string; sortOrder?: number }> = [],
  excludeId?: string,
  label = 'Sort order',
): string | undefined {
  if (isDuplicateSortOrder(sortOrder, records, excludeId)) {
    return `${label} is already used by another record`;
  }
  return undefined;
}
